import React, { useState, useEffect, useMemo } from 'react';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FILE_UPLOAD_HISTORY_ENDPOINT } from '../../config/urls';
import AppTable from '../../common/app-table';
import { formatWithTzLabel } from '../../utils/dateUtils';

dayjs.extend(customParseFormat);

const columns = [
  {
    id: 'upload_date',
    label: 'Upload Date',
    minWidth: 180,
    sortable: true,
    render: (row) => formatWithTzLabel(row.upload_date_iso, 'MMMM D, YYYY, h:mm A'),
  },
  {
    id: 'file_type',
    label: 'File Type',
    minWidth: 120,
    render: () => 'CSV',
  },
  {
    id: 'file_name',
    label: 'File Name',
    minWidth: 220,
    sortable: true,
  },
  {
    id: 'uploaded_by',
    label: 'Uploaded By',
    minWidth: 180,
    render: (row) => row.user?.name ?? '',
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 120,
    sortable: true,
    render: (row) =>
      row.status === 'success' ? (
        <Chip variant="outlined" color="success" label="Success" size="small" />
      ) : (
        <Chip variant="outlined" color="error" label="Failed" size="small" />
      ),
  },
  {
    id: 'message',
    label: 'Message',
    minWidth: 260,
  },
];

export default function FileUploadLogsTable({ refreshKey, dateInput }) {
  const [rowCount, setRowCount] = useState(0);
  const [uploadHistoryData, setUploadHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });
  const [sortState, setSortState] = useState([]);

  const sortedData = useMemo(() => {
    if (!sortState.length || !uploadHistoryData.length) return uploadHistoryData;
    return [...uploadHistoryData].sort((a, b) => {
      for (const { id, direction } of sortState) {
        let aVal = a[id];
        let bVal = b[id];
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === "asc" ? 1 : -1;
        if (bVal == null) return direction === "asc" ? -1 : 1;
        if (typeof aVal === "string") { aVal = aVal.toLowerCase(); bVal = String(bVal).toLowerCase(); }
        if (aVal < bVal) return direction === "asc" ? -1 : 1;
        if (aVal > bVal) return direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [uploadHistoryData, sortState]);

  const handleSortChange = (columnId) => {
    setSortState((prev) => {
      const existing = prev.find((s) => s.id === columnId);
      if (!existing) return [...prev, { id: columnId, direction: "asc" }];
      if (existing.direction === "asc") return prev.map((s) => s.id === columnId ? { ...s, direction: "desc" } : s);
      return prev.filter((s) => s.id !== columnId);
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      let searchDateURL = '';
      if (dateInput) {
        const dateString = dayjs(dateInput).format('YYYY-MM-DD');
        searchDateURL = `&search_date=${dateString}`;
      }

      const tenant = localStorage.getItem('tenant');
      const headers = {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenant,
      };

      try {
        const response = await fetch(
          `${FILE_UPLOAD_HISTORY_ENDPOINT}?limit=${controller.rowsPerPage}&offset=${controller.page * controller.rowsPerPage}${searchDateURL}`,
          { headers }
        );
        if (!response.ok) throw new Error('Failed to fetch data');
        const jsonData = await response.json();
        setUploadHistoryData(jsonData.results);
        setRowCount(jsonData.count);
      } catch (error) {
        console.error('Error fetching data:', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [controller, refreshKey, dateInput]);

  const handlePageChange = (event, newPage) => {
    setController((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
    }));
  };

  return (
    <AppTable
      columns={columns}
      rows={sortedData}
      rowKey={(row, index) => index}
      loading={loading}
      emptyMessage="No match found"
      maxHeight={500}
      containerSx={{
        scrollbarWidth: "thin",
        scrollbarColor: "#C5CEDB #F3F6FB",
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
        "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
      }}
      count={rowCount}
      page={controller.page}
      rowsPerPage={controller.rowsPerPage}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleChangeRowsPerPage}
      sortState={sortState}
      onSortChange={handleSortChange}
    />
  );
}